import { useEffect, useState } from "react";

export type ThemeMode = "light" | "dark" | "system";

export interface ThemeConfig {
  /** Current theme mode */
  mode: ThemeMode;
  /** Resolved theme (light or dark) after system preference is applied */
  resolvedTheme: "light" | "dark";
}

/**
 * Hook for managing widget theme
 *
 * Supports three modes:
 * - "light": Always use light theme
 * - "dark": Always use dark theme
 * - "system": Follow system preference (prefers-color-scheme)
 *
 * @param mode - The theme mode to use
 * @returns The resolved theme configuration
 */
export function useTheme(mode: ThemeMode = "light"): ThemeConfig {
  const [systemPrefersDark, setSystemPrefersDark] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  // Listen for system theme changes
  useEffect(() => {
    if (mode !== "system") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (event: MediaQueryListEvent) => {
      setSystemPrefersDark(event.matches);
    };

    // Modern browsers
    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, [mode]);

  // Resolve the actual theme
  const resolvedTheme: "light" | "dark" =
    mode === "system" ? (systemPrefersDark ? "dark" : "light") : mode;

  return {
    mode,
    resolvedTheme,
  };
}

/**
 * CSS variable overrides for custom theming
 */
export interface ThemeOverrides {
  /** Primary color (HSL values without hsl()) */
  primary?: string;
  /** Primary foreground color */
  primaryForeground?: string;
  /** Background color */
  background?: string;
  /** Foreground (text) color */
  foreground?: string;
  /** Border color */
  border?: string;
  /** Border radius */
  radius?: string;
  /** Font family */
  fontFamily?: string;
  /** CTA accent color (HSL values without hsl(), e.g. "210 90% 50%") */
  cta?: string;
}

/**
 * Generate CSS custom properties from theme overrides
 */
export function generateThemeStyles(overrides: ThemeOverrides): string {
  const properties: string[] = [];

  if (overrides.primary) {
    properties.push(`--apex-primary: ${overrides.primary}`);
  }
  if (overrides.primaryForeground) {
    properties.push(`--apex-primary-foreground: ${overrides.primaryForeground}`);
  }
  if (overrides.background) {
    properties.push(`--apex-background: ${overrides.background}`);
  }
  if (overrides.foreground) {
    properties.push(`--apex-foreground: ${overrides.foreground}`);
  }
  if (overrides.border) {
    properties.push(`--apex-border: ${overrides.border}`);
  }
  if (overrides.radius) {
    properties.push(`--apex-radius: ${overrides.radius}`);
  }
  if (overrides.fontFamily) {
    properties.push(`--apex-font-family: ${overrides.fontFamily}`);
  }
  if (overrides.cta) {
    properties.push(`--apex-accent-cta: ${overrides.cta}`);
    // Auto-derive hover variant (slightly darker)
    properties.push(`--apex-accent-cta-hover: ${overrides.cta}`);
  }

  return properties.join("; ");
}

/**
 * Inject theme overrides into an element's style
 */
export function applyThemeOverrides(
  element: HTMLElement,
  overrides: ThemeOverrides
): void {
  const styles = generateThemeStyles(overrides);
  if (styles) {
    element.style.cssText += styles;
  }
}
